/** 3PH Cloud field type resolution — shared by form preview and validators. */

export const THREE_PH_DATA_TYPES = [
  'singleselect',
  'multiselect',
  'header',
  'combo',
  'multilinetext',
  'hourmin',
  'minsec',
  'label',
  'remove',
] as const

export type ThreePhDataType = (typeof THREE_PH_DATA_TYPES)[number]

export interface ResolvedFieldType {
  /** Primary renderer key used by FormPreview */
  kind:
    | 'header'
    | 'label'
    | 'skip'
    | 'singleselect'
    | 'multiselect'
    | 'combo'
    | 'multilinetext'
    | 'hourmin'
    | 'minsec'
    | 'boolean'
    | 'date'
    | 'datetime'
    | 'time'
    | 'integer'
    | 'float'
    | 'text'
  dataType: string
  threePhDataType?: ThreePhDataType
}

export function parseThreePhDataType(raw: string | null | undefined): ThreePhDataType | undefined {
  if (!raw) return undefined
  const lower = raw.toLowerCase()
  return THREE_PH_DATA_TYPES.includes(lower as ThreePhDataType)
    ? (lower as ThreePhDataType)
    : undefined
}

export function resolveFieldType(
  dataType: string,
  threePhDataType?: string | null,
): ResolvedFieldType {
  const threePh = parseThreePhDataType(threePhDataType)
  const dt = (dataType || 'text').toLowerCase()

  if (threePh === 'header') return { kind: 'header', dataType: dt, threePhDataType: threePh }
  if (threePh === 'label') return { kind: 'label', dataType: dt, threePhDataType: threePh }
  if (threePh === 'remove') return { kind: 'skip', dataType: dt, threePhDataType: threePh }
  if (threePh === 'singleselect') return { kind: 'singleselect', dataType: dt, threePhDataType: threePh }
  if (threePh === 'multiselect') return { kind: 'multiselect', dataType: dt, threePhDataType: threePh }
  if (threePh === 'combo') return { kind: 'combo', dataType: dt, threePhDataType: threePh }
  if (threePh === 'multilinetext') return { kind: 'multilinetext', dataType: dt, threePhDataType: threePh }
  if (threePh === 'hourmin') return { kind: 'hourmin', dataType: dt, threePhDataType: threePh }
  if (threePh === 'minsec') return { kind: 'minsec', dataType: dt, threePhDataType: threePh }

  if (dt === 'boolean') return { kind: 'boolean', dataType: dt }
  if (dt === 'date') return { kind: 'date', dataType: dt }
  if (dt === 'datetime') return { kind: 'datetime', dataType: dt }
  if (dt === 'time') return { kind: 'time', dataType: dt }
  if (dt === 'integer') return { kind: 'integer', dataType: dt }
  if (dt === 'float' || dt === 'double') return { kind: 'float', dataType: dt }
  if (dt === 'singlelinetext' || dt === 'string' || dt === 'text') {
    return { kind: 'text', dataType: dt }
  }

  return { kind: 'text', dataType: dt }
}

/** 3PH multiselect: CodeListItem carries Alias Context = parent ItemDef OID */
export function codeListImpliesMultiselect(
  itemOid: string,
  codeListEl: Element | null,
): boolean {
  if (!codeListEl) return false
  for (const item of codeListEl.querySelectorAll('CodeListItem')) {
    for (const alias of item.querySelectorAll('Alias')) {
      if (alias.getAttribute('Context') === itemOid) return true
    }
  }
  return false
}
